import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import MuxPlayer from "@mux/mux-player-react";
import { useAuth } from "../App";
import { Lock, AlertCircle } from "lucide-react";

export const MuxLessonPlayer = ({ courseId, lessonId, title }) => {
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [needsSubscription, setNeedsSubscription] = useState(false);
  const [playerError, setPlayerError] = useState(false);

  useEffect(() => {
    let active = true;
    setData(null);
    setError(null);
    setNeedsSubscription(false);
    setPlayerError(false);

    const load = async () => {
      try {
        const res = await axios.post(`${API}/videos/playback-token`, {
          course_id: courseId,
          lesson_id: lessonId,
        });
        if (active) setData(res.data);
      } catch (e) {
        if (!active) return;
        const status = e.response?.status;
        setNeedsSubscription(status === 403);
        setError(e.response?.data?.detail || "No se pudo cargar el video");
      }
    };
    load();
    // Renovar el token antes de que expire para clases largas
    const interval = setInterval(load, 100 * 60 * 1000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [courseId, lessonId, API]);

  if (error || playerError) {
    const message = error || "El video de esta clase no está disponible en este momento. Avísale al administrador para que lo vuelva a cargar.";
    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center text-center px-6 bg-black"
        data-testid="mux-player-error"
      >
        {needsSubscription ? (
          <Lock className="w-10 h-10 text-[#E50914] mb-3" />
        ) : (
          <AlertCircle className="w-10 h-10 text-white/40 mb-3" />
        )}
        <p className="text-white font-semibold max-w-md">{message}</p>
        <div className="flex gap-3 mt-4">
          {!user && (
            <button
              onClick={() => navigate("/auth")}
              className="bg-white text-black px-5 py-2 rounded-md font-semibold hover:bg-white/90"
              data-testid="mux-login-btn"
            >
              Iniciar sesión
            </button>
          )}
          {needsSubscription && (
            <button
              onClick={() => navigate("/contaflix-pro")}
              className="bg-[#E50914] text-white px-5 py-2 rounded-md font-semibold hover:bg-[#E50914]/90"
              data-testid="mux-subscribe-btn"
            >
              Suscribirme a PRO
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-black" data-testid="mux-player-loading">
        <p className="text-white/60">Cargando video...</p>
      </div>
    );
  }

  return (
    <MuxPlayer
      playbackId={data.playback_id}
      tokens={data.tokens}
      metadata={{ video_title: title, viewer_user_id: user?.id }}
      accentColor="#E50914"
      maxResolution="1080p"
      streamType="on-demand"
      defaultShowCaptions
      onError={() => setPlayerError(true)}
      style={{
        width: "100%",
        height: "100%",
        "--seek-live-button": "none",
        "--media-object-fit": "contain",
      }}
      data-testid="mux-player"
    />
  );
};

export default MuxLessonPlayer;
